import {ApiClient} from '@hconnect/apiclient'
import type {Env} from '@hconnect/common/hproduce'
import {whiteListByPermission} from '@hconnect/common/hproduce'
import {FULL_COCKPIT_ACCESS_PERMISSION} from '@hconnect/common/hproduce/config/cockpit'
import {useLoginContext, useUserContext} from '@hconnect/login'
import {useUpmPlantsContext} from '@hconnect/upm'
import {usePermissionsParsedQuery} from '@hproduce/permissions'
import {useTranslation} from 'react-i18next'
import {BrowserRouter as Router} from 'react-router'

import {App} from './App'
import {HLog} from './common/utils/logging'
import {AccessType, getPlantId} from './common/utils/plantIdHelper'
import {PageLoadingSpinner} from './components/common/PageLoadingSpinner'
import {trackAnalyticsEvent} from './hooks/useTrackAnalyticsEvents'
import {PlantSelect} from './pages/PlantSelect'
import {SimpleErrorPage} from './pages/SimpleErrorPage'

const env = process.env.REACT_APP_STAGE as Env

export const AppWrapper: React.FC = () => {
  const {t} = useTranslation()
  const {loggedUser} = useUserContext()
  const {logout} = useLoginContext()
  const upmPlants = useUpmPlantsContext({isInitialLoad: true})

  const {data: permissions, error: permissionsError} = usePermissionsParsedQuery()

  if (permissionsError) {
    const errorMessage =
      permissionsError instanceof Error
        ? permissionsError.message
        : typeof permissionsError === 'string'
          ? permissionsError
          : 'Unknown error'

    HLog('cockpit').error(errorMessage)
    trackAnalyticsEvent('onErrorPageTracking', {errorMessage})
    return <SimpleErrorPage message={errorMessage} withRedirectButton={true} env={env} />
  }

  if (!loggedUser || !upmPlants || !permissions) {
    return <PageLoadingSpinner />
  }

  const whiteList = whiteListByPermission(env, permissions, upmPlants)

  if (!whiteList.optionsPerApp['Cockpit']) {
    trackAnalyticsEvent('onErrorPageTracking', {
      userId: loggedUser.id,
      errorMessage: 'Your account is missing the permission to view this report'
    })
    return <SimpleErrorPage message={t('error.missingPermission')} logout={() => void logout()} />
  }

  const {accessType, plantId} = getPlantId(window.location.pathname.split('/')[1], whiteList)

  if (accessType === AccessType.MultiplePlants) {
    return (
      <Router>
        <PlantSelect user={loggedUser} loginFlow={ApiClient.loginFlow} whiteList={whiteList} />
      </Router>
    )
  }

  if (!plantId) {
    throw new Error('BUG: PlantId should be defined at this point')
  }

  const hasFullCockpitAccess = permissions.some(
    (p) => p.permissionType === FULL_COCKPIT_ACCESS_PERMISSION
  )

  return (
    <App
      user={loggedUser}
      whiteList={whiteList}
      plantId={plantId}
      hasFullCockpitAccess={hasFullCockpitAccess}
    />
  )
}
